<template>
  <div class="ActiveTab ImageGallery">
    <LocatieInformatieHeader :chargingpoint="chargingpoint">
      <h4>{{ $t("components.sidebar.Tabs.Images.title") }}</h4>
    </LocatieInformatieHeader>

    <div class="ActiveTab__Content">
      <Toast group="tr" />
      <div
        v-if="initLoading"
        class="d-flex align-items-center justify-content-center mt-5"
      >
        <i
          class="far fa-3x fa-spinner fa-spin"
          style="--fa-animation-duration: 1.5s;"
        />
      </div>
      <div v-else>
        <ImageCarouselSection
          id="top-gallery"
          :loading="loading.top"
          :images="galleryImages.top"
          :uuid="chargingpoint.uuid"
          :has-cyclomedia-account="canGetFromCyclomedia"
          :already-generated="hasCyclomediaTopImages"
          view="top"
          title="Top Images"
          @update-images-top="(images) => updateGallery(images, 'top')"
          @delete-image-top="(image) => deleteGalleryImage(image, 'top')"
          @set-primary-top="(image) => setPrimary(image, 'top')"
          @get-from-cyclomedia="getFilesFromCyclomedia"
        />

        <div class="divider" />

        <ImageCarouselSection
          id="side-gallery"
          :loading="loading.side"
          :images="galleryImages.side"
          :uuid="chargingpoint.uuid"
          :has-cyclomedia-account="canGetFromCyclomedia"
          :already-generated="hasCyclomediaSideImages"
          view="side"
          title="Side Images"
          class="mt-4"
          @update-images-side="(images) => updateGallery(images, 'side')"
          @delete-image-side="(image) => deleteGalleryImage(image, 'side')"
          @set-primary-side="(image) => setPrimary(image, 'side')"
          @get-from-cyclomedia="getFilesFromCyclomedia"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import { getFileByUUID, deleteFile, updateImagesByView, uploadFile, getFilesFromCyclomedia } from '@/../shared/API/images'
import LocatieInformatieHeader from '@/components/map/sidebar/LocatieInformatieHeader'
import ImageCarouselSection from '../Components/ImageGallerySection.vue'
import Toast from 'primevue/toast'
import { getProvinceByMunicipalityCode, getCountryCodeByMunicipalityCode } from '@/data/gemeenten'
import { CYCLOMEDIA_ENABLED_CODES } from '@/data/cyclomedia'
import { insertComment } from '@/../shared/API/comments'
import imageCompression from 'browser-image-compression'

export default {
  name: 'ImageGallery',
  components: { LocatieInformatieHeader, ImageCarouselSection, Toast },

  data() {
    return {
      galleryImages: { top: [], side: [] },
      current: { top: [], side: [] },
      loading: { top: false, side: false },
      initLoading: false,
      activeLoadId: null, // 👈 track current loading session
      compressionOptions: {
        maxSizeMB: 3,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      },
    }
  },

  computed: {
    ...mapGetters('planmode',
      {
        getChargingPointIndexByRefId: 'getChargingPointIndexByRefId',
        chargingpoint: 'getSelectedChargingpoint',
      }),
    ...mapGetters('currentUser', {
      currentUserId: 'getCurrentUserId',
      currentUserName: 'getCurrentUserName',
    }),
    province() {
      return getProvinceByMunicipalityCode(this.chargingpoint.code)
    },

    country() {
      return getCountryCodeByMunicipalityCode(this.chargingpoint.code)
    },
    cyclomediaCode() {
      const municipalityCode = this.chargingpoint.code
      if (CYCLOMEDIA_ENABLED_CODES.includes(municipalityCode)) return municipalityCode

      const provinceCode = String(this.province)
      if (CYCLOMEDIA_ENABLED_CODES.includes(provinceCode)) return provinceCode

      return null
    },
    canGetFromCyclomedia() {
      return this.cyclomediaCode !== null
    },
    hasCyclomediaTopImages() {
      return this.chargingpoint.images?.top?.some(image => image?.metadata?.source === 'cyclomedia')
    },
    hasCyclomediaSideImages() {
      return this.chargingpoint.images?.side?.some(image => image?.metadata?.source === 'cyclomedia')
    },
  },
  watch: {
    chargingpoint(next, previous) {
      if (!next || next.uuid === previous?.uuid) return

      // 👇 create a new load session ID
      const loadId = (this.activeLoadId = Symbol('loadId'))

      const images = next.images
      if (!images) {
        this.current.top = this.current.side = []
        this.galleryImages.top = this.galleryImages.side = []
        return
      }

      this.current.top = images?.top || []
      this.current.side = images?.side || []

      Promise.all([
        this.loadImages('top', this.current.top, loadId),
        this.loadImages('side', this.current.side, loadId),
      ]).catch((err) => console.error('Error loading images:', err))
    },
  },

  async created() {
    this.initLoading = true
    this.current.top = this.chargingpoint?.images?.top || []
    this.current.side = this.chargingpoint?.images?.side || []

    await Promise.all([this.loadImages('top'), this.loadImages('side')])

    this.initLoading = false
  },

  methods: {
    ...mapMutations('planmode', ['updateChargingpoint']),

    async loadImages(view, updated = this.current[view], loadId = this.activeLoadId) {
      if (!updated?.length) {
        this.galleryImages[view] = []
        return
      }

      try {
        this.loading[view] = true
        const result = await Promise.all(
          updated.map(async (img) => ({
            ...img,
            url: await getFileByUUID(img.endpoint, img.index),
          })),
        )

        if (this.activeLoadId === loadId) {
          this.galleryImages[view] = result
          this.current[view] = updated
        }
      } finally {
        this.loading[view] = false
      }
    },

    async updateGallery(file, view, source = 'manual') {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png']

      if (!allowedTypes.includes(file.type)) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('components.sidebar.Tabs.Images.toasts.filetype.title'),
          detail: this.$t('components.sidebar.Tabs.Images.toasts.filetype.message'),
          group: 'tr',
          life: 10000,
        })

        return
      } else if ((this.chargingpoint.images?.[view]?.length || 0) >= 8) {
        this.$toast.add({
          severity: 'error',
          summary: this.$t('components.sidebar.Tabs.Images.toasts.maximum.title'),
          detail: this.$t('components.sidebar.Tabs.Images.toasts.maximum.message'),
          group: 'tr',
          life: 10000,
        })
        return
      }
      try {
        this.loading[view] = true

        const compressedFile = await imageCompression(file, this.compressionOptions)

        const { images } = await uploadFile(this.chargingpoint.uuid, compressedFile, view, source)

        await this.loadImages(view, images[view])
        this.updateLocation()
        if (this.galleryImages.top.length === 1) {
        await this.setPrimary(this.galleryImages.top[0], 'top')
      }
      if (this.galleryImages.side.length === 1) {
        await this.setPrimary(this.galleryImages.side[0], 'side')
      }
      } catch (error) {
        console.error('Error while uploading', error)
      } finally {
        this.loading[view] = false
      }
    },

    updateLocation() {
      this.chargingpoint.images = {
        top: this.current.top,
        side: this.current.side,
      }
      this.chargingpoint._id

      const index = this.getChargingPointIndexByRefId({
        _id: this.chargingpoint._id,
      })
      this.updateChargingpoint({ chargingpoint: this.chargingpoint, index })
    },

    async setPrimary(image, view) {
      try {
        const token = await this.$auth.getTokenSilently()

        const updated = await updateImagesByView({
          uuid: this.chargingpoint.uuid,
          imageId: image.index,
          view,
          token,
        })

        this.current[view] = updated.images[view]
        this.galleryImages[view] = updated.images[view].map((img) => ({
          ...img,
          url:
            this.galleryImages[view].find((g) => g.index === img.index)?.url ||
            '',
        }))
        this.updateLocation()
      } catch (err) {
        console.error('Error setting primary image:', err)
      }
    },
    async deleteGalleryImage(image, view) {
      try {
        this.loading[view] = true
        await deleteFile(this.chargingpoint.uuid, view, image.index)

        this.current[view] = this.current[view].filter(
          (img) => img.index !== image.index,
        )
        this.galleryImages[view] = this.galleryImages[view].filter(
          (img) => img.index !== image.index,
        )

        this.updateLocation()

        this.loading[view] = false
      } catch (err) {
        console.error('Failed to delete image:', err)
      } finally {
        this.loading[view] = false
      }
    },
    async getFilesFromCyclomedia(view) {
      this.loading[view] = true
      const coords = this.chargingpoint.coordinates
      if (!coords) return
      if (coords.length < 2) return
      coords[0] = parseFloat(coords[0])
      coords[1] = parseFloat(coords[1])
      const files = await getFilesFromCyclomedia(view, coords, this.cyclomediaCode, this.country).catch(() =>
          this.$toast.add({
          severity: 'error',
          summary: this.$t('components.sidebar.Tabs.Images.toasts.cyclomediaError.title'),
          detail: this.$t('components.sidebar.Tabs.Images.toasts.cyclomediaError.message'),
          group: 'tr',
          life: 10000,
        }))
      if (!files?.length) {
        this.loading[view] = false
        this.$toast.add({
          severity: 'info',
          summary: this.$t('components.sidebar.Tabs.Images.toasts.cyclomediaEmpty.title'),
          detail: this.$t('components.sidebar.Tabs.Images.toasts.cyclomediaEmpty.message'),
          group: 'tr',
          life: 10000,
        })
        return
      }
      for (const file of files) {
        await this.updateGallery(file, view, 'cyclomedia')
      }
      await insertComment({
        chargingPointUUID: this.chargingpoint.uuid,
        payload: {
          message_type: 'images_added_from_cyclomedia',
          message: this.$i18n.t('components.sidebar.Tabs.Images.comments.cyclomedia', { name: this.currentUserName }),
          comment_type: 'system',
          user: { name: this.currentUserName, id: this.currentUserId },
        },
      })
      this.loading[view] = false
    },
  },
}
</script>

<style scoped>
.ActiveTab__Content {
  margin-top: 1rem;
}

.divider {
  height: 1px;
  /* thin line */
  background-color: #ccc;
  /* light gray */
  margin: 2rem 0;
  /* spacing above and below */
  width: 100%;
  /* full width */
}
</style>
